import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { GameBase } from '../../games/game-base';
import { GameFactory } from '../../games/game-factory';
import { BaccaratTableComponent } from '../../games/main-games/baccarat/baccarat-table.component';
import { DragonTigerTableComponent } from '../../games/main-games/dragon-tiger/dragon-tiger-table.component';
import { ThreeHeadedDragonTableComponent } from '../../games/main-games/three-headed-dragon/three-headed-dragon-table.component';
import { AppPhase, DealingSubPhase, GameType } from '../../models/communication/app-enums.model';
import { TableUpdate } from '../../models/communication/app-state.model';
import { ResultChange } from '../../services/result-change.service';

/**
 * Generates the game table components based on the app state.
 * Initializes the game classes using the `GameFactory`.
 * Responsible for game instances' and relevant components' life cycles.
 *
 * It passes the relevant changes calling the `onChange` method.
 * Projects the `CountDownComponent` and `ResultChangeComponent`.
 */
@Component({
    selector: 'app-game-switch',
    standalone: true,
    imports: [CommonModule, DragonTigerTableComponent, BaccaratTableComponent, ThreeHeadedDragonTableComponent],
    templateUrl: './game-switch.component.html',
    styleUrl: './game-switch.component.scss'
})
export class GameSwitchComponent implements OnChanges {
    @Input()
    gameType!: GameType;

    @Input()
    tableUpdate?: TableUpdate;

    @Input()
    appPhase?: AppPhase;

    @Input()
    subPhase?: DealingSubPhase;

    protected readonly AppPhase = AppPhase;
    protected readonly GameType = GameType;

    protected game?: GameBase;

    constructor(
        private readonly gameFactory: GameFactory,
        protected readonly resultChange: ResultChange
    ) {}

    ngOnChanges(changes: Partial<SimpleChanges>): void {
        if (changes.gameType || (changes.appPhase && this.appPhase === AppPhase.NewRoundPreparation)) {
            this.initGame();
        }

        if (this.game) {
            if (changes.tableUpdate && this.tableUpdate) {
                this.game.flow.onChange({ action: 'tableChange', data: this.tableUpdate });
            }

            if (changes.subPhase && this.subPhase) {
                this.game.flow.onChange({ action: 'subPhaseChange', data: this.subPhase });
            }

            if (changes.appPhase && this.appPhase) {
                this.game.flow.onChange({ action: 'appPhaseChange', data: this.appPhase });
            }
        }
    }

    private initGame(): void {
        this.game = this.gameFactory.create(this.gameType);
        this.resultChange.setGame(this.game);
    }
}
